import { captureSession, isSessionCurrent } from '../utils/authSession';
import { notifyFeedbackActionCompleted } from '../utils/feedbackEvents';
import React, { useState, useEffect, useRef } from 'react';
import orderService from '../services/orderService';
import productService from '../services/productService';
import clinicService from '../services/clinicService';
import { stateCode } from '../data/usStates';
import { previewShipping } from '../services/shippingService';
import {
  buildOrderPayload,
  MAX_LINE_ITEMS,
  sizeFromProductName,
} from '../features/orderEntry/orderPayload';
import { useAuth } from '../context/AuthContext';
import { isAdmin } from '../utils/roles';
import { requestErrorMessage } from '../utils/requestError';
import { useT, useLocale } from '../i18n/LanguageContext';
import OrderDeliveryFields from '../features/orderEntry/OrderDeliveryFields';
import useAccessibleDialog from '../hooks/useAccessibleDialog';

/** Web order creation using the shared order and versioned shipping contracts. */

const inputClass =
  'w-full h-[40px] border border-[#d6dce8] rounded-[8px] px-3 text-[13px] text-[#0f172a] placeholder:text-[#97a3b6] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]';

const selectClass =
  'w-full h-[40px] border border-[#d6dce8] rounded-[8px] px-2 text-[13px] text-[#0f172a] bg-white focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]';

const emptyLine = () => ({ product: '', quantity: '1', size: '' });

export default function NewOrderModal({ open, onClose, onCreated }) {
  const t = useT();
  const locale = useLocale();
  const { user } = useAuth();
  const admin = isAdmin(user);
  // A clinic-site login (My Hub, 9/4/26). Its clinic row carries the rep's two controls:
  // orderingEnabled (false = read-only clinic) and allowedProductCount (null = every product).
  // Reps and admins see neither - for them the form is exactly what it was.
  const clinicLogin = user?.role === 'clinic';
  const [products, setProducts] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [productError, setProductError] = useState('');
  const [sizesError, setSizesError] = useState('');
  const [clinics, setClinics] = useState([]);
  const [clinic, setClinic] = useState('');
  // 'loading' | 'loaded' | 'error' - a fetch error must not read as "no
  // clinic needed". See the fail-closed gate below.
  const [clinicLoad, setClinicLoad] = useState('loading');
  const [patientName, setPatientName] = useState('');
  const [lineItems, setLineItems] = useState([emptyLine()]);
  const [address, setAddress] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressState, setAddressState] = useState('');
  const [addressZipCode, setAddressZipCode] = useState('');
  const [addressEdited, setAddressEdited] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [shippingSpeed, setShippingSpeed] = useState('');
  const [preferredCarrier, setPreferredCarrier] = useState('fedex');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const shippingSequence = useRef(0);
  const lastPreference = useRef(null);
  const [shippingState, setShippingState] = useState('empty');
  const [shippingNotice, setShippingNotice] = useState('');
  const [shippingHelpOpen, setShippingHelpOpen] = useState(false);
  useEffect(() => {
    return () => {
      shippingSequence.current += 1;
    };
  }, [open, user?._id, user?.id]);

  const applyShipping = (quote, changedAtSubmit = false) => {
    setShippingSpeed(quote.shippingSpeed);
    setDeliveryDate(quote.deliveryDate);
    setShippingState('ready');
    setShippingNotice(
      changedAtSubmit
        ? 'Shipping availability changed. Review the updated delivery date, then select Create Order again.'
        : ''
    );
  };
  const updateShipping = async (preference) => {
    if (submittingRef.current) return;
    const sequence = ++shippingSequence.current;
    lastPreference.current = preference;
    setShippingNotice('');
    if (!preference.shippingSpeed) {
      setShippingSpeed('');
      setDeliveryDate('');
      setShippingState('empty');
      return;
    }
    setDeliveryDate('');
    setShippingState('loading');
    try {
      const quote = await previewShipping(preference);
      if (sequence === shippingSequence.current) applyShipping(quote);
    } catch {
      if (sequence === shippingSequence.current) setShippingState('error');
    }
  };

  useEffect(() => {
    if (!open) return;
    let active = true;
    // House envelope pattern: res.data?.data?.data || res.data?.data || []
    productService
      .getActiveProducts()
      .then((res) => {
        const data = res.data?.data?.data || res.data?.data || [];
        setProducts(Array.isArray(data) ? data : []);
        setProductError(
          Array.isArray(data) && data.length ? '' : 'No products are available to order.'
        );
      })
      .catch(() => {
        setProducts([]);
        // NOT silent any more. This catch turned a 403 into an empty dropdown, indistinguishable
        // from "the catalog is empty" - which is why a rep could not order and nothing said why.
        setProductError('Could not load products. Reload the page, or contact an administrator.');
      });
    clinicService
      .getSizes()
      .then((res) => {
        // Envelope-tolerant: /sizes returns { names: [...] } (optionally wrapped in data).
        const body = res.data?.data || res.data || {};
        const names = Array.isArray(body.names) ? body.names : [];
        setSizes(names);
        setSizesError('');
      })
      .catch(() => {
        // Without the size list every product would be blamed for "not stating its size". Say what
        // actually failed instead.
        setSizes([]);
        setSizesError(
          'Could not load the product sizes. Reload the page, or contact an administrator.'
        );
      });
    setClinicLoad('loading');
    clinicService
      .getClinics()
      .then((res) => {
        if (!active) return;
        const data = res.data?.data?.data || res.data?.data || [];
        const list = Array.isArray(data) ? data : [];
        setClinics(list);
        setClinicLoad('loaded');
        if (list.length === 1) setClinic(list[0]._id); // clinic login: locked to its own
      })
      .catch(() => {
        if (active) {
          setClinics([]);
          setClinicLoad('error');
        }
      });
    return () => {
      active = false;
    };
  }, [open]);

  // Changing clinics replaces the whole destination. Shipping choice and other inputs stay put.
  useEffect(() => {
    const saved = clinics.find((item) => item._id === clinic)?.deliveryAddress;
    setAddress(saved?.street || '');
    setAddressCity(saved?.city || '');
    setAddressState(stateCode(saved?.state));
    setAddressZipCode(saved?.zipCode || '');
    setPreferredCarrier(
      clinics.find((item) => item._id === clinic)?.defaultPreferredCarrier || 'fedex'
    );
    setAddressEdited(false);
  }, [clinic, clinics]);

  // Clear a line when its product leaves the available catalog. Size travels with the product.
  useEffect(() => {
    setLineItems((prev) => {
      const stale = (item) => item.product && !products.some((p) => p._id === item.product);
      if (!prev.some(stale)) return prev;
      return prev.map((item) => (stale(item) ? { ...item, product: '', size: '' } : item));
    });
  }, [clinic, products]);

  const reset = () => {
    setClinic('');
    setClinicLoad('loading');
    setPatientName('');
    setLineItems([emptyLine()]);
    setAddress('');
    setAddressCity('');
    setAddressState('');
    setAddressZipCode('');
    setAddressEdited(false);
    setDeliveryDate('');
    setShippingSpeed('');
    setPreferredCarrier('fedex');
    shippingSequence.current += 1;
    lastPreference.current = null;
    setShippingState('empty');
    setShippingNotice('');
    setShippingHelpOpen(false);

    setComment('');
  };

  // Cancel, the X, the backdrop and Escape all leave the form EMPTY (9/3/26). They used to call
  // onClose alone, so reopening showed the half-filled previous order.
  const close = () => {
    if (submittingRef.current) return;
    reset();
    onClose();
  };
  const { dialogRef, titleId } = useAccessibleDialog({
    open,
    onClose: close,
    closeDisabled: submitting,
  });

  if (!open) return null;

  // A valid order requires a clinic, complete product lines, and confirmed shipping details.
  const lineItemsValid =
    lineItems.length > 0 &&
    lineItems.every((item) => item.product && item.size && parseInt(item.quantity, 10) >= 1);
  // Clinic loading and assignment failures block submission instead of creating a tenantless order.
  const repHasNoClinics = !admin && clinicLoad === 'loaded' && clinics.length === 0;
  const clinicSatisfied = !!clinic;
  // My Hub (9/4/26): a clinic login whose rep switched ordering off cannot submit - the server
  // refuses the order anyway; this says so before the click. The product count is informational:
  // GET /products is already filtered to the allowed ones, so the picker is short and this
  // explains why.
  const selectedClinic = clinics.find((c) => c._id === clinic);
  const orderingOff = clinicLogin && selectedClinic?.orderingEnabled === false;
  // The clinic row's count includes ids of products deactivated since the rep picked them; the
  // picker (GET /products, filtered server-side to the live allowed ones) is the truth, so the
  // sentence counts what the person can actually see.
  const allowedProductCount =
    clinicLogin && typeof selectedClinic?.allowedProductCount === 'number'
      ? products.length > 0
        ? products.length
        : selectedClinic.allowedProductCount
      : null;
  // Customer PO is optional. The legacy patientName key remains in the API payload.
  const savedAddress = selectedClinic?.deliveryAddress;
  const useDefaultAddress = () => {
    setAddress(savedAddress?.street || '');
    setAddressCity(savedAddress?.city || '');
    setAddressState(stateCode(savedAddress?.state));
    setAddressZipCode(savedAddress?.zipCode || '');
    setAddressEdited(false);
  };
  const hasDeliveryAddress =
    (addressEdited && !!savedAddress) ||
    [address, addressCity, addressState, addressZipCode].some((value) => value.trim());
  const addressValid =
    !hasDeliveryAddress ||
    [address, addressCity, addressState, addressZipCode].every((value) => value.trim());
  const canSubmit =
    addressValid &&
    clinicSatisfied &&
    lineItemsValid &&
    !!preferredCarrier &&
    !!deliveryDate &&
    shippingState === 'ready' &&
    !submitting &&
    !orderingOff;

  const setLine = (index, patch) => {
    setLineItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const handleSubmit = async () => {
    const feedbackSession = captureSession();
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    const sequence = shippingSequence.current;
    try {
      // Re-read server availability even when this form was opened before the cutoff.
      const quote = await previewShipping({ shippingSpeed });
      if (sequence !== shippingSequence.current) return;
      if (quote.deliveryDate !== deliveryDate || quote.shippingSpeed !== shippingSpeed) {
        applyShipping(quote, true);
        return;
      }
      await orderService.createOrder(
        buildOrderPayload({
          patientName,
          lineItems,
          // Untouched defaults retain the PDF's original two address lines and metadata.
          // Edits use the existing versioned shipping/address contract.
          address: addressEdited ? address : '',
          addressCity: addressEdited ? addressCity : '',
          addressState: addressEdited ? addressState : '',
          addressZipCode: addressEdited ? addressZipCode : '',
          deliveryDate,
          shippingSpeed,
          preferredCarrier,
          shippingScheduleVersion: 1,
          comment,
          clinic,
        })
      );
      if (sequence !== shippingSequence.current) return;
      reset();
      onCreated();
      onClose();
      if (isSessionCurrent(feedbackSession)) notifyFeedbackActionCompleted();
    } catch (err) {
      if (sequence !== shippingSequence.current) return;
      // A 409 occurs before persistence if the cutoff changed during the request.
      if (err?.response?.status === 409) {
        try {
          const quote = await previewShipping({ shippingSpeed });
          if (sequence === shippingSequence.current) applyShipping(quote, true);
        } catch {
          if (sequence === shippingSequence.current) setShippingState('error');
        }
      } else {
        alert(t(requestErrorMessage(err, 'Failed to create order')));
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={close} data-testid="modal-backdrop" />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative bg-white rounded-[14px] shadow-xl w-[560px] max-w-[calc(100vw-2rem)] z-10 max-h-[90vh] overflow-y-auto"
      >
        <div className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px] px-5 py-4 flex items-center justify-between">
          <h2 id={titleId} className="text-[16px] font-semibold text-[#0f172a]">
            {t('New Order')}
          </h2>
          <button
            onClick={close}
            aria-label={t('Close')}
            className="w-6 h-6 flex items-center justify-center text-[#64748b] hover:text-[#0f172a]"
          >
            <span className="material-symbols-outlined text-[24px]">cancel</span>
          </button>
        </div>

        <fieldset disabled={submitting} className="p-5 min-w-0">
          {clinicLoad === 'error' && (
            <div
              role="alert"
              className="mb-4 px-3 py-2 rounded-[8px] bg-[rgba(254,226,226,0.6)] text-[12px] text-[#f23e41]"
            >
              {t(
                'Could not load clinics. Close and reopen to retry - submission is blocked until the clinic list loads.'
              )}
            </div>
          )}
          {repHasNoClinics && (
            <div
              role="alert"
              className="mb-4 px-3 py-2 rounded-[8px] bg-[rgba(254,226,226,0.6)] text-[12px] text-[#f23e41]"
            >
              {t('No clinics are assigned to you. Contact an administrator before submitting.')}
            </div>
          )}
          {orderingOff && (
            <div
              role="alert"
              className="mb-4 px-3 py-2 rounded-[8px] bg-[rgba(254,226,226,0.6)] text-[12px] text-[#f23e41]"
            >
              {t(
                'Ordering is turned off for your clinic. Your sales representative places orders for you - you can still see every order here.'
              )}
            </div>
          )}

          {clinics.length > 0 && (
            <div className="mb-4">
              <p className="text-[12px] font-medium text-[#0f172a] mb-1">
                {t('Clinic')} <span className="text-[#f23e41]">*</span>
              </p>
              <select
                value={clinic}
                onChange={(e) => setClinic(e.target.value)}
                aria-label={t('Clinic')}
                disabled={clinics.length === 1}
                className={selectClass + ' disabled:bg-[#f7f8fb]'}
              >
                <option value="">{t('Select clinic...')}</option>
                {clinics.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* New orders do not collect a provider. Historical provider data remains read-only. */}
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div>
              <label
                htmlFor="new-order-customer-po"
                className="block text-[12px] font-medium text-[#0f172a] mb-1"
              >
                {t('Customer PO')}
              </label>
              <input
                id="new-order-customer-po"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
                placeholder={t('Customer PO')}
                className={inputClass}
              />
            </div>
          </div>

          <div className="mb-4">
            {productError && (
              <div className="mb-2 px-3 py-2 rounded-[8px] bg-[rgba(254,226,226,0.6)] text-[12px] text-[#f23e41]">
                {t(productError)}
              </div>
            )}
            {!productError && sizesError && (
              <div
                role="alert"
                className="mb-2 px-3 py-2 rounded-[8px] bg-[rgba(254,226,226,0.6)] text-[12px] text-[#f23e41]"
              >
                {t(sizesError)}
              </div>
            )}
            <div className="flex items-center justify-between mb-1">
              <p className="text-[12px] font-medium text-[#0f172a]">
                {t('Products')} <span className="text-[#f23e41]">*</span>
              </p>
              <button
                onClick={() =>
                  setLineItems((prev) =>
                    prev.length < MAX_LINE_ITEMS ? [...prev, emptyLine()] : prev
                  )
                }
                disabled={lineItems.length >= MAX_LINE_ITEMS}
                className={`text-[12px] font-medium ${
                  lineItems.length >= MAX_LINE_ITEMS
                    ? 'text-[#97a3b6] cursor-not-allowed'
                    : 'text-[#0089ff] hover:text-[#0077e6]'
                }`}
              >
                {t('+ Add Product')}
              </button>
            </div>
            <div className="space-y-3">
              {lineItems.map((item, i) => (
                // Size is derived from the selected product and remains part of the line-item payload.
                <div key={i} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <select
                      value={item.product}
                      onChange={(e) => {
                        const id = e.target.value;
                        const picked = products.find((p) => p._id === id);
                        // Size travels WITH the product - the name already states it.
                        setLine(i, { product: id, size: sizeFromProductName(picked?.name, sizes) });
                      }}
                      aria-label={t('Product {n}', { n: i + 1 })}
                      className={selectClass + ' flex-1 min-w-[200px]'}
                    >
                      <option value="">{t('Select product...')}</option>
                      {products.map((p) => (
                        <option key={p._id} value={p._id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => setLine(i, { quantity: e.target.value })}
                      aria-label={t('Quantity {n}', { n: i + 1 })}
                      className="w-[70px] h-[40px] border border-[#d6dce8] rounded-[8px] px-3 text-[13px] text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
                    />
                    <button
                      onClick={() => setLineItems((prev) => prev.filter((_, idx) => idx !== i))}
                      disabled={lineItems.length <= 1}
                      aria-label={t('Remove product {n}', { n: i + 1 })}
                      className={`w-6 h-6 flex items-center justify-center rounded ${
                        lineItems.length <= 1
                          ? 'text-[#e2e8f0] cursor-not-allowed'
                          : 'text-[#64748b] hover:text-[#f23e41] hover:bg-gray-50'
                      }`}
                    >
                      <span className="material-symbols-outlined text-[16px]">close</span>
                    </button>
                  </div>
                  {/* Explain unresolved sizes before the user attempts to submit. */}
                  {item.product && !item.size && (
                    <p className="text-[11px] text-[#f23e41]">
                      {t(
                        'This product does not state its size in its name, so it cannot be ordered here. Tell MedEffects.'
                      )}
                    </p>
                  )}
                </div>
              ))}
            </div>
            {allowedProductCount !== null && (
              <p className="text-[11px] text-[#64748b] mt-2">
                {allowedProductCount === 1
                  ? t('Your sales representative limited this clinic to {n} product', {
                      n: allowedProductCount,
                    })
                  : t('Your sales representative limited this clinic to {n} products', {
                      n: allowedProductCount,
                    })}
              </p>
            )}
          </div>

          <OrderDeliveryFields
            t={t}
            locale={locale}
            inputClass={inputClass}
            selectClass={selectClass}
            address={address}
            setAddress={setAddress}
            addressCity={addressCity}
            setAddressCity={setAddressCity}
            addressState={addressState}
            setAddressState={setAddressState}
            addressZipCode={addressZipCode}
            setAddressZipCode={setAddressZipCode}
            addressEdited={addressEdited}
            setAddressEdited={setAddressEdited}
            savedAddress={savedAddress}
            hasDeliveryAddress={hasDeliveryAddress}
            useDefaultAddress={useDefaultAddress}
            preferredCarrier={preferredCarrier}
            setPreferredCarrier={setPreferredCarrier}
            submitting={submitting}
            shippingHelpOpen={shippingHelpOpen}
            setShippingHelpOpen={setShippingHelpOpen}
            shippingSpeed={shippingSpeed}
            setShippingSpeed={setShippingSpeed}
            updateShipping={updateShipping}
            deliveryDate={deliveryDate}
            shippingState={shippingState}
            shippingNotice={shippingNotice}
            onRetryShipping={() => updateShipping(lastPreference.current || { shippingSpeed })}
          />

          <div className="mb-5">
            <label
              htmlFor="new-order-comment"
              className="block text-[12px] font-medium text-[#0f172a] mb-1"
            >
              {t('Comment')}
            </label>
            <textarea
              id="new-order-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('Notes for this order...')}
              className="w-full h-[60px] border border-[#d6dce8] rounded-[8px] px-3 py-2 text-[13px] text-[#0f172a] placeholder:text-[#97a3b6] resize-none focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
            />
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              onClick={close}
              className="text-[13px] font-medium text-[#64748b] hover:text-[#0f172a] transition-colors"
            >
              {t('Cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`h-[36px] px-5 rounded-[6px] text-[13px] font-medium transition-colors ${
                canSubmit
                  ? 'bg-[#0089ff] text-white hover:bg-[#0077e6]'
                  : 'bg-[#e2e8f0] text-[#97a3b6] cursor-not-allowed'
              }`}
            >
              {submitting ? t('Creating...') : t('Create Order')}
            </button>
          </div>
        </fieldset>
      </div>
    </div>
  );
}
